import React, { useLayoutEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import AuthScreen from './AuthScreen';
import CreateAccount from './CreateAccount';
import DesignerDashboard from './DesignerDashboard';
import PlanUpload from './PlanUpload';
import PlanDetail from './PlanDetail';
import MasterPlantList from './MasterPlantList';
import DesignerMasterPlantList from './DesignerMasterPlantList';
import SharedPlanView from './SharedPlanView';
import CommunicationCentre from './components/CommunicationCentre';
import Plants from './Plants';
import FindDesigner from './FindDesigner';
import YardAnalysis from './YardAnalysis';
import DesignerProfile from './DesignerProfile';
import Suppliers from './Suppliers';
import AdminLogin from './AdminLogin';
import AdminDashboard from './AdminDashboard';
import TermsOfService from './TermsOfService';
import Symbols from './Symbols';
import HomePage from './pages/GardenerHome';
import LearnPage from './pages/LearnPage';
import LearnArticleStub from './pages/LearnArticleStub';
import WatchPage from './pages/WatchPage';
import CommunityPage from './pages/CommunityPage';
import YardIdeasPage from './pages/YardIdeasPage';
import AskPage from './pages/AskPage';
import DearJohnPage from './pages/DearJohnPage';
import MyQuestionsPage from './pages/MyQuestionsPage';
import YardProfilePage from './pages/YardProfilePage';
import HomeownerBriefPage from './pages/HomeownerBriefPage';
import LisaPage from './pages/LisaPage';
import GardenApp from './garden/GardenApp';
import InviteLanding from './handoff/InviteLanding';
import ResetPassword from './ResetPassword';

// Component to track page views for Google Analytics
function LoginRoute({ onLoginSuccess }) {
  const location = useLocation();
  const handleLogin = (user) => onLoginSuccess(user, location.state);
  return <AuthScreen onLoginSuccess={handleLogin} />;
}

function PageViewTracker() {
  const location = useLocation();

  React.useEffect(() => {
    // Wait for gtag to be available (script loads asynchronously)
    const trackPageView = () => {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('config', 'G-KR2MSWNWHV', {
          page_path: location.pathname + location.search,
          page_title: document.title,
        });
        console.log('GA Page View:', location.pathname + location.search);
      } else {
        // Retry after a short delay if gtag isn't ready yet
        setTimeout(trackPageView, 100);
      }
    };

    // Small delay to ensure gtag is loaded
    const timeoutId = setTimeout(trackPageView, 100);
    
    return () => clearTimeout(timeoutId);
  }, [location]);

  return null;
}

/** Reset scroll on route changes (pathname/search). Hash-only navigations are unchanged so in-page anchors still work. */
function ScrollToTop() {
  const { pathname, search } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);
  return null;
}

function PlantADayApp() {
  // Initialize auth state synchronously from localStorage to prevent flashing
  const [isAuthenticated, setIsAuthenticated] = React.useState(() => {
    if (typeof window !== 'undefined') {
      const token = localStorage.getItem('authToken');
      const user = localStorage.getItem('user');
      return !!(token && user);
    }
    return false;
  });
  
  // Initialize currentUser synchronously from localStorage
  const [currentUser, setCurrentUser] = React.useState(() => {
    if (typeof window !== 'undefined') {
      const user = localStorage.getItem('user');
      if (user) {
        try {
          return JSON.parse(user);
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  });

  // Verify token is still valid on mount (optional - can be removed if not needed)
  React.useEffect(() => {
    // Only verify if we think we're authenticated
    if (isAuthenticated) {
      const token = localStorage.getItem('authToken');
      const user = localStorage.getItem('user');
      
      // If token or user was removed, update state
      if (!token || !user) {
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
    }
  }, []);

  const handleLogin = (user, navState = {}) => {
    // Token is already stored by AuthScreen, just need to update state
    setIsAuthenticated(true);
    setCurrentUser(user);
    // Also store as 'user' for compatibility
    localStorage.setItem('user', JSON.stringify(user));
    
    // Redirect based on user role
    if (user.role === 'customer') {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('plantaday_openIdentify') === '1') {
        window.location.href = '/plants';
        return;
      }
      const ret = navState?.returnTo;
      if (typeof ret === 'string' && ret.startsWith('/')) {
        window.location.href = ret;
        return;
      }
      // Logged-in gardeners land in their garden by default rather than
      // the marketing landing page.
      window.location.href = '/garden';
    } else {
      window.location.href = '/dashboard';
    }
  };

  const handleLogout = () => {
    // Clear localStorage first
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('userData');
    localStorage.removeItem('plantaday_viewMode');
    // Force full page reload to reset all React state
    window.location.href = '/';
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      <PageViewTracker />
      <Routes>
        {/* Public routes */}
        <Route
          path="/"
          element={
            isAuthenticated && currentUser?.role === 'customer'
              ? <Navigate to="/garden" replace />
              : <HomePage />
          }
        />
        
        <Route 
          path="/login" 
          element={
            isAuthenticated ? 
              (currentUser?.role === 'customer' ? 
                <Navigate to="/garden" replace /> : 
                <Navigate to="/dashboard" replace />) : 
              <LoginRoute onLoginSuccess={handleLogin} />
          } 
        />
        
        <Route 
          path="/create-account" 
          element={
            isAuthenticated ? 
              (currentUser?.role === 'customer' ? 
                <Navigate to="/garden" replace /> : 
                <Navigate to="/dashboard" replace />) : 
              <CreateAccount />
          } 
        />
        
        <Route 
          path="/admin" 
          element={
            <AdminLogin 
              onLoginSuccess={(admin) => {
                // Navigate to admin dashboard on successful login
                window.location.href = '/admin/dashboard';
              }} 
            />
          } 
        />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        
        {/* Public shared project view */}
        <Route path="/shared/:token" element={<SharedPlanView />} />
        <Route path="/shared/:token/communication" element={<CommunicationCentre />} />

        {/* Protected routes */}
        <Route 
          path="/dashboard" 
          element={
            isAuthenticated ? 
              <DesignerDashboard onLogout={handleLogout} /> : 
              <Navigate to="/" replace />
          } 
        />
        
        <Route 
          path="/upload-plan" 
          element={
            isAuthenticated ? 
              <PlanUpload /> : 
              <Navigate to="/" replace />
          } 
        />
        
        <Route 
          path="/plan/:id" 
          element={
            isAuthenticated ? 
              <PlanDetail /> : 
              <Navigate to="/" replace />
          } 
        />
        
        <Route 
          path="/plan/:id/communication" 
          element={
            isAuthenticated ? 
              <CommunicationCentre /> : 
              <Navigate to="/" replace />
          } 
        />
        
        <Route 
          path="/plan/:id/master-list" 
          element={
            isAuthenticated ? 
              <MasterPlantList /> : 
              <Navigate to="/" replace />
          } 
        />

        <Route
          path="/master-plant-list"
          element={
            isAuthenticated ?
              <DesignerMasterPlantList /> :
              <Navigate to="/" replace />
          }
        />
        
        <Route 
          path="/suppliers" 
          element={
            isAuthenticated ? 
              <Suppliers /> : 
              <Navigate to="/" replace />
          } 
        />

        <Route
          path="/symbols"
          element={
            isAuthenticated ?
              <Symbols /> :
              <Navigate to="/" replace />
          }
        />
        
        <Route 
          path="/profile" 
          element={
            (isAuthenticated || localStorage.getItem('adminToken')) ? 
              <DesignerProfile /> : 
              <Navigate to="/" replace />
          } 
        />
        
        {/* Plants routes - publicly accessible */}
        <Route path="/plants" element={<Plants />} />
        {/* Guided lookup lives on Yard Ideas; keep URL for bookmarks */}
        <Route
          path="/plants/discover"
          element={<Navigate to={{ pathname: '/ideas', hash: '#guided-lookup' }} replace />}
        />
        <Route path="/find-designer" element={<FindDesigner />} />
        <Route path="/send-brief/:designerId" element={<HomeownerBriefPage />} />
        {/* Yard Analysis now lives on /ideas - keep URL for bookmarks */}
        <Route path="/yard-analysis" element={<Navigate to="/ideas" replace />} />

        <Route path="/ideas" element={<YardIdeasPage />} />
        <Route path="/dashboard/my-questions" element={<MyQuestionsPage />} />
        <Route path="/yard-profile" element={<YardProfilePage />} />
        <Route path="/learn" element={<LearnPage />} />
        <Route path="/learn/dear-john" element={<DearJohnPage />} />
        <Route path="/learn/:slug" element={<LearnArticleStub />} />
        <Route path="/watch" element={<WatchPage />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/lisa" element={<LisaPage />} />

        {/* /ask-ai/:id and /ask/:id - shareable permalink for a saved answer */}
        <Route path="/ask-ai/:id" element={<AskPage />} />
        <Route path="/ask/:id" element={<AskPage />} />
        <Route path="/ask-ai" element={<Navigate to="/ideas" replace />} />
        {/* /ask with no id redirects to /ideas (handled inside AskPage too) */}
        <Route path="/ask" element={<Navigate to="/ideas" replace />} />

        {/* Garden handoff invite landing — public (no auth required).
            Must be declared BEFORE the /garden/* catch-all so unauthenticated
            invitees aren't bounced to /login. */}
        <Route path="/garden/invite/:token" element={<InviteLanding />} />

        {/* Everyday Gardener mode — isolated from designer system */}
        <Route
          path="/garden/*"
          element={
            isAuthenticated ?
              <GardenApp /> :
              <Navigate to="/login" state={{ returnTo: '/garden' }} replace />
          }
        />

        {/* Password reset - reached via link in the forgot-password email.
            Always accessible (even if already authenticated) in case the
            user is signed in on another device and needs to update here. */}
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Legal pages */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<TermsOfService />} />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Analytics />
      <SpeedInsights />
    </BrowserRouter>
  );
}

export default PlantADayApp;
